import { ChatMessageResponse, ChatMessagesResponse, MarkAsReadRequest } from "@/models/forms";
import api from "@/services/api";
import popupService from "@/services/popupService";
import { FC, useEffect, useState, useRef } from "react";
import BaseInput from "@/components/common/baseInput";
import { InputAdornment } from "@mui/material";
import AttachFile from "@mui/icons-material/AttachFile";
import Close from "@mui/icons-material/Close";
import swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import fileIcon from "@/assets/icons/file_icon.svg";

interface IFormsChatProps {
    userName: string | undefined;
    formId: number;
}

const FormsChat: FC<IFormsChatProps> = (props: IFormsChatProps): JSX.Element => {
    const { t } = useTranslation();
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
    const uploadFileInput = useRef<HTMLInputElement>(null);
    const chatContent = useRef<HTMLDivElement>(null);
    const [chatMessages, setChatMessages] = useState<ChatMessageResponse[]>([]);
    const [allChatMessages, setAllChatMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [messageSent, setMessageSent] = useState<boolean>(false);
    const [lastUnreadMessage, setLastUnreadMessage] = useState<ChatMessageResponse | null>(null);

    async function getChatMessages(): Promise<ChatMessagesResponse> {
        const responsePromise = api.getChatMessages(props.formId);
        return (await responsePromise).data;
    }

    function callUploadingFunction(): void {
        uploadFileInput.current?.click();
        removeAllFiles();
    }
    function removeFile(index: number) {
        const newArr: File[] = filesToUpload.slice();
        newArr.splice(index, 1);
        setFilesToUpload(newArr);
    }
    const removeAllFiles = () => {
        if (uploadFileInput && uploadFileInput.current && uploadFileInput.current.value) {
            uploadFileInput.current.value = "";
        }
        setFilesToUpload([]);
    };
    async function scrollBottom() {
        const container = document.getElementById("forms-chat-content");
        const lastUnreadedMessage = container?.getElementsByClassName("un-read");
        if (lastUnreadedMessage && lastUnreadedMessage.length > 0) {
            lastUnreadedMessage[0].scrollIntoView({ behavior: "smooth" });
            await markAsRead();
        } else {
            container?.lastElementChild?.scrollIntoView({ behavior: "smooth" });
        }
    }
    async function sendMessage() {
        if (!message) {
            return;
        }
        const fd = new FormData();
        filesToUpload.forEach((file) => {
            fd.append("files", file, file.name);
        });
        fd.append("message", message);
        fd.append("formId", props.formId.toString());
        try {
            const config = {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            };
            swal.showLoading(null);
            await api.sendMessage(config, fd);
        } catch (error: any) {
            swal.fire({
                icon: "error",
                text: error,
            });
        }
        removeAllFiles();
        setMessage("");
        setMessageSent(!messageSent);

        swal.close();
    }
    async function uploadingFileValidation(uploadingFiles: any): Promise<void> {
        const allFiles = Object.entries(uploadingFiles);
        const filesArray: File[] = [];
        if (allFiles.length > 0) {
            allFiles.forEach((element: any) => {
                filesArray.push(element[1]);
            });
        }
        const fileTypes: string[] = [
            "doc",
            "docx",
            "xls",
            "xlsx",
            "txt",
            "pdf",
            "png",
            "ppt",
            "jpg",
            "gif",
            "mp3",
            "mp4",
        ];
        filesArray.forEach((file) => {
            const fileExt = file.name.split(".").pop();
            if (!fileTypes.includes(fileExt!)) {
                swal.fire({
                    icon: "error",
                    text: t("forms.incorrectFileType") as string,
                });
                removeAllFiles();
                return;
            }
            if (file.size > 5242880) {
                swal.fire({
                    icon: "error",
                    text: t("forms.incorrectFileSize") as string,
                });
                removeAllFiles();
                return;
            }
        });

        setFilesToUpload(filesArray);
    }
    function getLastUnreadMessage(msgArr: ChatMessageResponse[]) {
        if (msgArr.length === 0) {
            return;
        }
        const foundFirstUnreadMsg = msgArr.find((x) => x.boUserId && !x.read);
        if (foundFirstUnreadMsg) {
            setLastUnreadMessage(foundFirstUnreadMsg);
        } else {
            setLastUnreadMessage(null);
        }
    }

    useEffect(() => {
        const timer = setInterval(async () => {
            await startFetching();
        }, 60000);

        return () => {
            clearInterval(timer);
        };
    }, []);

    async function startFetching() {
        try {
            const data = await getChatMessages();
            const apiResponse = data;
            setChatMessages(apiResponse.chatMessages);
            getLastUnreadMessage(apiResponse.chatMessages);
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    }

    async function markAsRead() {
        if (!lastUnreadMessage) {
            return;
        }
        try {
            const req = new MarkAsReadRequest();
            req.FormId = props.formId;
            req.MessageId = lastUnreadMessage.id;
            await api.markAsRead(req);
            const chatMsgs = chatMessages.slice();
            const found = chatMsgs.find((x) => x.boUserId && !x.read);
            if (found) {
                found.read = true;
            }
            setChatMessages(chatMsgs);
            setLastUnreadMessage(null);
        } catch (error: any) {
            swal.fire({
                icon: "error",
                text: error,
            });
        }
    }

    useEffect(() => {
        setTimeout(async () => {
            await scrollBottom();
        }, 500);
    }, [chatMessages]);

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [props.formId, messageSent]);

    async function onContentScroll() {
        if (!chatContent) {
            return;
        }
        const contentDiv = chatContent.current;
        if (!contentDiv) {
            return;
        }
        const isBottom = contentDiv.scrollHeight - contentDiv.scrollTop - contentDiv.clientHeight;
        if (isBottom === 0) {
            getLastUnreadMessage(chatMessages);
            await markAsRead();
        }
    }

    return (
        <div className="forms-chat-container">
            <div className="forms-chat-header">
                <div style={{ display: "flex", alignItems: "center" }} className="font-style-white-bold">
                    {props.userName}
                </div>
            </div>
            <div
                className="forms-chat-content"
                ref={chatContent}
                id="forms-chat-content"
                onScroll={() => onContentScroll()}
            >
                {chatMessages?.map((message, mindex) => (
                    <div key={mindex} className={`${message.boUserId && !message.read ? "un-read" : "read"}`}>
                        <div
                            className="forms-chat-messages"
                            style={
                                message.boUserId == null
                                    ? { justifyContent: "flex-end", textAlign: "end" }
                                    : { justifyContent: "flex-start" }
                            }
                            key={message.id}
                        >
                            <div
                                className={
                                    message.boUserId == null ? "user-forms-chat-message" : "employee-forms-chat-message"
                                }
                                key={message.id}
                            >
                                {message.message}
                            </div>
                        </div>
                        {message.files?.map((file, findex) => (
                            <div
                                key={findex}
                                className="forms-chat-messages message-file"
                                style={
                                    message.boUserId == null
                                        ? { justifyContent: "flex-end" }
                                        : { justifyContent: "flex-start" }
                                }
                            >
                                <div
                                    className={`msg-text-block ${message.boUserId == null ? "user" : "employee"}`}
                                    key={`block-${findex}`}
                                >
                                    <a style={{ display: "inline-flex" }} href={file.pathUrl}>
                                        <img
                                            style={{ marginInlineEnd: "8px", width: "22px" }}
                                            src={fileIcon}
                                            alt="icon"
                                        />
                                        <span className="msg-text" style={{ alignSelf: "center" }}>
                                            {file.fileName}
                                        </span>
                                    </a>
                                    {/* <div>
                                            <span v-if="row.message" class="msg-text">{{ row.message }}</span>
                                        </div> */}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
                {filesToUpload.map((file, index) => (
                    <div key={`file-${index}`} className="download-file-container">
                        <div style={{ maxWidth: "90%" }}>
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">({(file.size / (1024 * 1024)).toFixed(2) + "Mb"})</span>
                        </div>
                        <Close onClick={() => removeFile(index)} style={{ cursor: "pointer" }} />
                    </div>
                ))}
            </div>
            <div className="forms-chat-message-input">
                <div>
                    <BaseInput
                        className="chat-message-input-style"
                        type="search"
                        size="small"
                        onChange={(event) => setMessage(event.target.value)}
                        value={message}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <input
                                        onChange={(event) => uploadingFileValidation(event.target.files)}
                                        style={{ display: "none" }}
                                        id="uploadFileInput"
                                        ref={uploadFileInput}
                                        multiple
                                        type="file"
                                    />
                                    {
                                        <AttachFile
                                            onClick={() => callUploadingFunction()}
                                            style={{ cursor: "pointer" }}
                                        />
                                    }
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: "100%%",
                        }}
                    />
                </div>
                <div className="form-chat-message-input-button" onClick={() => sendMessage()}>
                    <i className="icon-f icon-ChatSendMsgIcon fs-24" />
                </div>
            </div>
        </div>
    );
};

export default FormsChat;
